/**
 * Deterministic technical-authority projection for documents expressly
 * incorporated by a verified RFQ.
 *
 * This module deliberately creates technical/method authority only. It never
 * turns a design note into billable scope. Every extracted requirement must be
 * dispositioned separately before a downstream stage may treat the projection
 * as complete.
 */

import { sourceBoundarySubjectForLineage } from './incorporated-lineage-semantics.js';

type LooseRecord = Record<string, unknown>;

export const INCORPORATED_AUTHORITY_LIMITS = Object.freeze({
    // Compatibility surface for older provenance callers. Incorporated PDF
    // ingestion is completeness-bound, not size-bound; prompt transport is
    // chunked separately below.
    maxSources: Number.POSITIVE_INFINITY,
    maxSourceCharsPerDocument: Number.POSITIVE_INFINITY,
    maxTotalSourceChars: Number.POSITIVE_INFINITY,
    maxRequirementChars: Number.POSITIVE_INFINITY,
    maxRequirements: Number.POSITIVE_INFINITY,
    maxProjectionChars: Number.POSITIVE_INFINITY,
});

/** Maximum size of one transport chunk, never a whole-document ingestion ceiling. */
export const INCORPORATED_AUTHORITY_PROMPT_PROJECTION_MAX_CHARS = 32_000;
const INCORPORATED_AUTHORITY_PROMPT_REQUIREMENT_FRAGMENT_CHARS = 8_000;

export const INCORPORATED_REQUIREMENT_DISPOSITION_STATUSES = Object.freeze([
    'included_in_builder_scope',
    'excluded_by_source_boundary',
    'coordination_only',
    'awaiting_builder_decision',
    'not_applicable',
] as const);

export type IncorporatedRequirementDispositionStatus =
    typeof INCORPORATED_REQUIREMENT_DISPOSITION_STATUSES[number];

export type IncorporatedAuthorityCue =
    | 'action'
    | 'normative'
    | 'construction'
    | 'named_system'
    | 'measurement'
    | 'source_boundary'
    | 'test_hold_point'
    | 'technical_sequence'
    | 'technical_annotation';

export type IncorporatedMeasurementKind =
    | 'length'
    | 'area'
    | 'count'
    | 'volume'
    | 'mass'
    | 'duration'
    | 'fall_ratio'
    | 'fall_percent';

export interface IncorporatedTechnicalDocumentInput {
    readonly sourceId: string;
    readonly sourceName?: string;
    readonly name?: string;
    /** Full, normalised extracted text. This function never accepts excerpts. */
    readonly content: string;
    /** Fingerprint of the full source, not of a prompt-sized excerpt. */
    readonly sourceContentFingerprint: string;
    readonly relationshipFingerprint: string;
    readonly referenceStatementFingerprint?: string;
    readonly classification?: string;
    /** Optional full normalised length, used to detect a sliced persisted copy. */
    readonly sourceLength: number;
    readonly normalizedSourceLength?: number;
    /** Exact PDF page attestation when the source has pages; null for text-only sources. */
    readonly sourcePageCount?: number | null;
    readonly extractedPageCount?: number | null;
    readonly complete: boolean;
    readonly contentComplete?: boolean;
    readonly truncated: boolean;
    readonly contentTruncated?: boolean;
}

export interface IncorporatedAuthorityMeasurementV1 {
    readonly version: 1;
    readonly id: string;
    readonly sourceRef: string;
    readonly sourceId: string;
    readonly text: string;
    readonly kind: IncorporatedMeasurementKind;
    readonly value?: number;
    readonly unit?: string;
    readonly numerator?: number;
    readonly denominator?: number;
    readonly canonical: string;
    readonly sourceStart: number;
    readonly sourceEnd: number;
}

export interface IncorporatedAuthorityRequirementV1 {
    readonly version: 1;
    readonly id: string;
    readonly authority: 'technical-method-only-not-billable';
    readonly sourceId: string;
    readonly sourceName: string;
    readonly sourceContentFingerprint: string;
    readonly relationshipFingerprint: string;
    readonly ordinal: number;
    /** One-based PDF page parsed from the extraction marker, when available. */
    readonly pageOrdinal: number | null;
    /** Complete semantic requirement text. Never ellipsised or prompt-truncated. */
    readonly text: string;
    readonly cues: readonly IncorporatedAuthorityCue[];
    readonly byOthers: boolean;
    readonly sourceBoundary: 'by_others' | null;
    readonly sourceRef: string;
    readonly sourceSnippet: Readonly<{
        text: string;
        start: number;
        end: number;
        lineStart: number;
        lineEnd: number;
    }>;
    readonly measurements: readonly IncorporatedAuthorityMeasurementV1[];
}

export interface IncorporatedAuthoritySourceV1 {
    readonly version: 1;
    readonly sourceId: string;
    readonly sourceName: string;
    readonly classification: string;
    readonly relationshipFingerprint: string;
    readonly referenceStatementFingerprint: string | null;
    readonly sourceLength: number;
    readonly sourceFingerprint: string;
    readonly pageCount: number | null;
    readonly extractedPageCount: number | null;
    readonly pageOrdinals: readonly number[];
    readonly projectionLength: number;
    readonly projectionFingerprint: string;
    readonly requirementCount: number;
    readonly complete: true;
    readonly truncated: false;
}

export interface IncorporatedAuthoritySnapshotV1 {
    readonly version: 1;
    readonly kind: 'incorporated-technical-authority-snapshot';
    readonly authority: 'technical-method-only-not-billable';
    readonly complete: true;
    readonly truncated: false;
    readonly sourceCount: number;
    readonly requirementCount: number;
    readonly sourceLength: number;
    readonly sourceFingerprint: string;
    readonly projectionLength: number;
    readonly projectionFingerprint: string;
    readonly snapshotFingerprint: string;
    readonly sources: readonly IncorporatedAuthoritySourceV1[];
    readonly requirements: readonly IncorporatedAuthorityRequirementV1[];
}

export type IncorporatedAuthorityBuildFailureCode =
    | 'no-sources'
    | 'too-many-sources'
    | 'source-metadata-invalid'
    | 'duplicate-source-id'
    | 'duplicate-source-content'
    | 'source-content-empty'
    | 'source-content-truncated'
    | 'source-content-over-limit'
    | 'source-content-fingerprint-mismatch'
    | 'requirement-over-limit'
    | 'no-requirements'
    | 'too-many-requirements'
    | 'projection-over-limit';

export interface IncorporatedAuthorityBuildFailure {
    readonly version: 1;
    readonly complete: false;
    readonly truncated: boolean;
    readonly code: IncorporatedAuthorityBuildFailureCode;
    readonly message: string;
    readonly sourceId?: string;
}

export type IncorporatedAuthorityBuildResult =
    | Readonly<{ ok: true; snapshot: IncorporatedAuthoritySnapshotV1 }>
    | Readonly<{ ok: false; failure: IncorporatedAuthorityBuildFailure }>;

export interface IncorporatedAuthorityPromptProjectionV1 {
    readonly version: 1;
    readonly kind: 'incorporated-authority-prompt-projection';
    readonly complete: true;
    readonly truncated: false;
    readonly snapshotFingerprint: string;
    readonly sourceCount: number;
    readonly requirementCount: number;
    readonly chunkCount: number;
    readonly projectionLength: number;
    readonly projectionFingerprint: string;
    /** Complete text for a one-chunk projection; empty for a chunk-manifest projection. */
    readonly text: string;
    readonly chunks: readonly IncorporatedAuthorityPromptProjectionChunkV1[];
    readonly coverageReceipt: IncorporatedAuthorityPromptCoverageReceiptV1;
}

export interface IncorporatedAuthorityPromptProjectionChunkV1 {
    readonly version: 1;
    readonly kind: 'incorporated-authority-prompt-projection-chunk';
    readonly complete: true;
    readonly truncated: false;
    readonly snapshotFingerprint: string;
    readonly chunkId: string;
    readonly chunkOrdinal: number;
    readonly chunkCount: number;
    readonly sourceIds: readonly string[];
    readonly requirementOrdinals: readonly number[];
    readonly requirementIds: readonly string[];
    readonly requirementCount: number;
    readonly requirementFragmentCount: number;
    readonly pageOrdinals: readonly number[];
    readonly projectionLength: number;
    readonly projectionFingerprint: string;
    /** Compact JSON containing this chunk's exact requirement text fragments. */
    readonly text: string;
}

export interface IncorporatedAuthorityPromptCoverageReceiptV1 {
    readonly version: 1;
    readonly kind: 'incorporated-authority-prompt-coverage-receipt';
    readonly complete: true;
    readonly truncated: false;
    readonly snapshotFingerprint: string;
    readonly sourceCount: number;
    readonly sourceLength: number;
    readonly sourceFingerprint: string;
    readonly requirementCount: number;
    readonly chunkCount: number;
    readonly projectionLength: number;
    readonly projectionFingerprint: string;
    readonly coveredRequirementOrdinals: readonly number[];
    readonly sources: readonly Readonly<{
        sourceId: string;
        sourceLength: number;
        sourceFingerprint: string;
        pageCount: number | null;
        extractedPageCount: number | null;
        pageOrdinals: readonly number[];
        requirementCount: number;
        sourceRequirementOrdinals: readonly number[];
        projectionRequirementOrdinals: readonly number[];
        chunkOrdinals: readonly number[];
    }>[];
    readonly chunks: readonly Readonly<{
        chunkId: string;
        chunkOrdinal: number;
        projectionLength: number;
        projectionFingerprint: string;
        sourceIds: readonly string[];
        sourceCoverage: readonly Readonly<{
            sourceId: string;
            sourceLength: number;
            sourceFingerprint: string;
            pageOrdinals: readonly number[];
            sourceRequirementOrdinals: readonly number[];
            projectionRequirementOrdinals: readonly number[];
        }>[];
        requirementOrdinals: readonly number[];
        requirementIds: readonly string[];
        requirementCount: number;
        requirementFragmentCount: number;
    }>[];
    readonly coverageFingerprint: string;
}

export type IncorporatedAuthorityPromptProjectionResult =
    | Readonly<{ ok: true; projection: IncorporatedAuthorityPromptProjectionV1 }>
    | Readonly<{
        ok: false;
        failure: Readonly<{
            version: 1;
            complete: false;
            truncated: false;
            code: 'snapshot-invalid' | 'projection-chunk-invalid';
            message: string;
            chunkOrdinal?: number;
            projectionLength?: number;
        }>;
    }>;

export type IncorporatedAuthorityPromptProjectionValidationResult =
    | Readonly<{ ok: true; receipt: IncorporatedAuthorityPromptCoverageReceiptV1 }>
    | Readonly<{
        ok: false;
        failure: Readonly<{
            version: 1;
            complete: false;
            truncated: false;
            code:
                | 'snapshot-invalid'
                | 'projection-invalid'
                | 'chunk-count-mismatch'
                | 'chunk-missing'
                | 'chunk-fingerprint-mismatch'
                | 'requirement-coverage-missing'
                | 'requirement-coverage-duplicate'
                | 'coverage-receipt-mismatch';
            message: string;
            expectedChunkCount?: number;
            receivedChunkCount?: number;
            chunkOrdinal?: number;
            requirementOrdinal?: number;
        }>;
    }>;

export interface IncorporatedRequirementDispositionInput {
    readonly requirement_id?: unknown;
    readonly status?: unknown;
    readonly activity_refs?: unknown;
    readonly pricing_input_refs?: unknown;
    readonly reason?: unknown;
}

export interface IncorporatedRequirementDispositionV1 {
    readonly version: 1;
    readonly requirement_id: string;
    readonly status: IncorporatedRequirementDispositionStatus;
    readonly activity_refs: readonly string[];
    readonly pricing_input_refs: readonly string[];
    readonly reason: string;
}

export interface IncorporatedRequirementDispositionReceiptV1 {
    readonly version: 1;
    readonly kind: 'incorporated-requirement-disposition-receipt';
    readonly complete: true;
    readonly snapshotFingerprint: string;
    readonly requirementCount: number;
    readonly dispositionCount: number;
    readonly dispositionFingerprint: string;
    readonly dispositions: readonly IncorporatedRequirementDispositionV1[];
}

export type IncorporatedDispositionIssueCode =
    | 'snapshot-invalid'
    | 'disposition-not-object'
    | 'requirement-id-missing'
    | 'unknown-requirement'
    | 'duplicate-requirement'
    | 'conflicting-requirement'
    | 'missing-requirement'
    | 'invalid-status'
    | 'by-others-included'
    | 'included-activity-refs-missing'
    | 'included-pricing-input-refs-missing'
    | 'non-included-pricing-input-refs-present'
    | 'non-coordination-activity-refs-present'
    | 'coordination-activity-refs-missing'
    | 'not-applicable-reason-missing'
    | 'source-boundary-status-invalid'
    | 'builder-work-status-invalid'
    | 'invalid-activity-refs'
    | 'invalid-pricing-input-refs'
    | 'invalid-reason';

export interface IncorporatedDispositionIssueV1 {
    readonly version: 1;
    readonly code: IncorporatedDispositionIssueCode;
    readonly message: string;
    readonly requirementId?: string;
    readonly index?: number;
}

export type IncorporatedDispositionValidationResult =
    | Readonly<{ ok: true; receipt: IncorporatedRequirementDispositionReceiptV1 }>
    | Readonly<{
        ok: false;
        failure: Readonly<{
            version: 1;
            complete: false;
            issues: readonly IncorporatedDispositionIssueV1[];
        }>;
    }>;

type SourceSegment = {
    text: string;
    sourceText: string;
    start: number;
    end: number;
    lineStart: number;
    lineEnd: number;
};

type RawMeasurement = Omit<IncorporatedAuthorityMeasurementV1, 'version' | 'id' | 'sourceRef' | 'sourceId'>;

type ValidatedSource = {
    input: IncorporatedTechnicalDocumentInput;
    sourceId: string;
    sourceName: string;
    classification: string;
    content: string;
    sourceFingerprint: string;
    pageCount: number | null;
    extractedPageCount: number | null;
    relationshipFingerprint: string;
    referenceStatementFingerprint: string | null;
};

const DISPOSITION_STATUS_SET = new Set<string>(INCORPORATED_REQUIREMENT_DISPOSITION_STATUSES);
const EXPLICIT_TRUNCATION_RE = /\[(?:source|content|text)?\s*(?:was\s+)?(?:truncated|omitted)\]|\b(?:source|content|text)\s+truncated\b/i;
const STRUCTURAL_PREFIX_RE = /^\s*(?:(?:#{1,6}|[-*•▪◦]+)\s*|(?:\d{1,3}|[A-Za-z])[.)]\s*)/;
const ADMINISTRATIVE_LINE_RE = /^\s*(?:drawing\s+(?:no|number|title|scale)|project\s+(?:no|number)|revision|rev\.?|date|drawn|checked|approved|sheet|page|client|address)\s*[:#-]/i;

const ACTION_RE = /\b(?:apply|applied|application|attach(?:ed)?|bonded|build|carried|clean|complete|construct|coordinate|covered|cut|demolish|disconnect|engineer(?:ed)?|ensure|erect|extend(?:ed)?|fabricate|fit(?:ted)?|fix(?:ed)?|form|grade(?:d)?|install(?:ed|ation|ing)?|lap(?:ped)?|lay|laid|locate(?:d)?|make\s+good|new|patch(?:ed)?|prepare|preparation|prime|protect(?:ed|ion)?|provide|record(?:ed)?|reinforc(?:e|ed|ement|ing)|reinstate|relocate|remov(?:e|al)|repair(?:ed)?|replace|retain|review|seal(?:ed)?|secure(?:d)?|set|size(?:d)?|strip|submit(?:ted)?|supply|terminate|termination|test(?:ed|ing)?|torch(?:ed|-on)?|turn(?:ed)?|waterproof|weld(?:ed)?)\b/i;
const INDEPENDENT_CLAUSE_START_RE = /^(?:and\s+)?(?:apply|attach|build|construct|coordinate|cut|demolish|engineer|erect|fabricate|fix|form|install|new|prepare|prime|protect|provide|reinstate|relocate|remove|replace|seal|supply|terminate|test|waterproof)\b/i;
const NORMATIVE_RE = /\b(?:as\s+per|in\s+accordance\s+with|manufacturer(?:'s)?|minimum|maximum|must|not\s+less\s+than|prior\s+to|required|requirements?|shall|should|specification|to\s+be\s+confirmed|verify)\b/i;
const SOURCE_BOUNDARY_RE = /\b(?:by\s+(?:others|owner|client|principal|separate\s+contractor)|not\s+by\s+(?:the\s+)?builder|excluded\s+from\s+(?:the\s+)?builder(?:'s)?\s+scope|n\.?i\.?c\.?)\b/i;
const ADMINISTRATIVE_SOURCE_BOUNDARY_RE = /\b(?:approved|checked|drawn|issued|prepared|reviewed|signed|verified)\s+by\s+(?:owner|client|principal)\b|\b(?:deposit|fees?|invoice|payment)\b.{0,40}\bby\s+(?:owner|client|principal)\b/i;
const ORPHAN_SOURCE_BOUNDARY_PREDICATE_RE = /^(?:(?:to\s+)?be|is|are|shall\s+be|will\s+be)\s+(?:attached|built|constructed|disconnected|erected|fabricated|installed|provided|reinstated|replaced|supplied)(?:\s+and\s+(?:attached|built|constructed|disconnected|erected|fabricated|installed|provided|reinstated|replaced|supplied))*\s+by\s+(?:others|owner|client|principal|separate\s+contractor)\b/i;
const NEGATED_SOURCE_BOUNDARY_RE = /\b(?:is\s+|shall\s+)?not\s+(?:(?:to\s+)?be\s+)?by\s+(?:others|owner|client|principal|separate\s+contractor)\b|\bisn['’]t\s+(?:to\s+be\s+|be\s+)?by\s+(?:others|owner|client|principal|separate\s+contractor)\b|\b(?:is\s+|shall\s+)?not\s+(?:(?:to\s+)?be\s+)?excluded\s+from\s+(?:the\s+)?builder(?:'s)?\s+scope\b/i;
const TEST_HOLD_POINT_RE = /\b(?:flood\s+test|hold\s+point|inspection|manufacturer\s+review|sign[ -]?off|water\s+test|witness)\b/i;
const KNOWN_NAMED_SYSTEM_RE = /\b(?:ALSAN|ARDEX|Bostik|Fosroc|GR\s+Cap|Mapei|Parchem|Sika|SOPREMA|Tremco|Wolfin)\b/i;
const GENERIC_NAMED_SYSTEM_RE = /\b(?:[A-Z]{2,}[A-Za-z0-9®™-]*|[A-Z][A-Za-z0-9®™-]*[0-9®™][A-Za-z0-9®™-]*|[A-Z][a-z][A-Za-z0-9®™-]+\s+[A-Z][A-Za-z0-9®™-]{1,})(?:\s+[A-Z0-9][A-Za-z0-9®™-]{1,}){0,2}\s+(?:coating|membrane|primer|sealant|system|waterstop)\b/;

const CONSTRUCTION_TERM_SOURCE = [
    'adhesive', 'anchor', 'backing rod', 'balustrade', 'balstrade', 'base sheet',
    'angle', 'bedding', 'bond breaker', 'cap sheet', 'capping', 'coat', 'coating',
    'concrete', 'conduit',
    'control joint', 'corner', 'door', 'drain', 'drainage', 'drainage cell', 'duct',
    'fabric', 'fillet', 'flange', 'flashing', 'floor', 'frame', 'geotextile', 'grate', 'grout',
    'hob', 'joint', 'lap', 'membrane', 'outlet', 'overflow', 'panel', 'paver', 'pedestal',
    'penetration', 'pipe', 'ply', 'primer', 'privacy screen', 'protection board',
    'layer', 'reinforcement', 'render', 'riser', 'roof', 'screed', 'sealant', 'sheet',
    'sill', 'slab', 'sleeve', 'steel',
    'soffit', 'strip', 'substrate', 'tape', 'termination bar', 'threshold', 'tile',
    'topping', 'upstand', 'upturn', 'vent', 'wall', 'waterproof', 'waterstop', 'weep',
    'window', 'fall', 'flood test', 'hold point',
].map(value => value.replace(/ /g, '\\s+')).join('|');
const CONSTRUCTION_RE = new RegExp(`\\b(?:${CONSTRUCTION_TERM_SOURCE})(?:s|ing|ed)?\\b`, 'i');
const CONSTRUCTION_TERMS_RE = new RegExp(`\\b(?:${CONSTRUCTION_TERM_SOURCE})(?:s|ing|ed)?\\b`, 'gi');

const BASIC_MEASUREMENT_RE = /(?<![A-Za-z0-9])((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)\s*-?\s*(mm|millimet(?:re|er)s?|cm|centimet(?:re|er)s?|m²|m2|sqm|sq\.?\s*m|square\s+met(?:re|er)s?|m|met(?:re|er)s?|panels?|sheets?|items?|each|ea|bags?|lit(?:re|er)s?|ltr?s?|l|kg|kgs|kilograms?|hours?|hrs?)(?![A-Za-z0-9])/gi;
const DOCUMENTED_MEASUREMENT_CUE_RE = /(?<![A-Za-z0-9])(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?\s*-?\s*(?:mm|millimet(?:re|er)s?|cm|centimet(?:re|er)s?|m²|m2|sqm|sq\.?\s*m|square\s+met(?:re|er)s?|m|met(?:re|er)s?|panels?|sheets?|items?|each|ea|bags?|lit(?:re|er)s?|ltr?s?|l|kg|kgs|kilograms?|hours?|hrs?)(?![A-Za-z0-9])/i;
const PACKAGE_MULTIPLIER_RE = /(?<![A-Za-z0-9])((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)\s*(?:x|×)\s*((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)\s*-?\s*(lit(?:re|er)s?|ltr?s?|l|kg|kgs|kilograms?)\s*(bags?|packs?|packets?|pails?|tubs?|buckets?|containers?|drums?|cans?|kits?|tins?|bottles?)?(?![A-Za-z0-9])/gi;
const FALL_COLON_RATIO_RE = /\b(?:fall(?:s)?(?:\s+(?:of|at|to))?\s*)?(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)(?:\s+fall(?:s)?)?\b/gi;
const FALL_IN_RATIO_RE = /\b(?:fall(?:s)?(?:\s+(?:of|at|to))?\s*)?(\d+(?:\.\d+)?)\s+in\s+(\d+(?:\.\d+)?)(?:\s+fall(?:s)?)?\b/gi;
const FALL_PERCENT_RE = /\bfall(?:s)?(?:\s+(?:of|at|to))?\s*(\d+(?:\.\d+)?)\s*%|\b(\d+(?:\.\d+)?)\s*%\s*fall(?:s)?\b/gi;

/**
 * The existing provenance layer uses the same q m-content-v1 checksum shape.
 * Keeping the implementation here removes an import cycle when the provenance
 * layer later calls this projection utility.
 */
export function fingerprintIncorporatedAuthorityContent(value: unknown): string {
    const text = normaliseAuthorityText(value);
    let first = 0x811c9dc5;
    let second = 0x9e3779b9;
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        first ^= code;
        first = Math.imul(first, 0x01000193);
        second ^= code + index;
        second = Math.imul(second, 0x85ebca6b);
        second ^= second >>> 13;
    }
    return `qm-content-v1:${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`;
}

export function normaliseAuthorityText(value: unknown): string {
    if (value == null) return '';
    return String(value)
        .normalize('NFKC')
        .replace(/\r\n?/g, '\n')
        .replace(/[\t\f\v ]+/g, ' ')
        .replace(/ *\n */g, '\n')
        .trim();
}

function freezeDeep<T>(value: T): T {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    for (const child of Object.values(value as LooseRecord)) freezeDeep(child);
    return Object.freeze(value);
}

function buildFailure(
    code: IncorporatedAuthorityBuildFailureCode,
    message: string,
    options: { sourceId?: string; truncated?: boolean } = {},
): IncorporatedAuthorityBuildResult {
    return freezeDeep({
        ok: false as const,
        failure: {
            version: 1 as const,
            complete: false as const,
            truncated: options.truncated === true,
            code,
            message,
            ...(options.sourceId ? { sourceId: options.sourceId } : {}),
        },
    });
}

function sentenceBoundary(line: string, index: number): boolean {
    const character = line[index];
    if (character === ';' || character === '!' || character === '?') return true;
    if (character !== '.') return false;
    const before = line[index - 1] || '';
    const after = line[index + 1] || '';
    if (/\d/.test(before) && /\d/.test(after)) return false;
    const prefix = line.slice(0, index + 1).trim();
    if (/^(?:\d{1,3}|[A-Za-z])\.$/.test(prefix)) return false;
    if (/\b(?:no|rev|fig|dr|mr|ms|st|min|max|typ)\.$/i.test(prefix)) return false;
    return index === line.length - 1 || /\s/.test(after);
}

function mixedSourceBoundaryClause(line: string, index: number): boolean {
    if (line[index] !== ',') return false;
    const sentenceEnd = line.slice(index + 1).search(/[;.!?]/);
    const tail = line.slice(index + 1, sentenceEnd >= 0 ? index + 1 + sentenceEnd : undefined).trim();
    const independentPredicate = INDEPENDENT_CLAUSE_START_RE.test(tail)
        || /\bto\s+be\s+(?:attached|built|constructed|installed|provided|replaced)\b/i.test(tail);
    return independentPredicate && hasPositiveSourceBoundary(tail) && CONSTRUCTION_RE.test(tail);
}

function semanticSentenceSegments(
    sourceBlock: string,
    absoluteStart: number,
    blockLineStart: number,
): SourceSegment[] {
    const segments: SourceSegment[] = [];
    // Replacing each layout newline with one space preserves character indexes
    // while allowing sentence segmentation to see PDF-wrapped lines as one
    // complete clause.
    const semanticBlock = sourceBlock.replace(/\n/g, ' ');
    let segmentStart = 0;
    const push = (rawEnd: number) => {
        let start = segmentStart;
        let end = rawEnd;
        while (start < end && /\s/.test(semanticBlock[start] || '')) start += 1;
        while (end > start && /\s/.test(semanticBlock[end - 1] || '')) end -= 1;
        const sourceText = sourceBlock.slice(start, end);
        const text = semanticBlock.slice(start, end)
            .replace(STRUCTURAL_PREFIX_RE, '')
            .replace(/^\|\s*|\s*\|$/g, '')
            .replace(/\s*\|\s*/g, ' — ')
            .replace(/\s+/g, ' ')
            .replace(/\s+([,.;:!?])/g, '$1')
            .trim();
        if (text) {
            const priorLines = (sourceBlock.slice(0, start).match(/\n/g) || []).length;
            const spannedLines = (sourceText.match(/\n/g) || []).length;
            segments.push({
                text,
                sourceText,
                start: absoluteStart + start,
                end: absoluteStart + end,
                lineStart: blockLineStart + priorLines,
                lineEnd: blockLineStart + priorLines + spannedLines,
            });
        }
    };

    for (let index = 0; index < semanticBlock.length; index += 1) {
        const character = semanticBlock[index];
        if (mixedSourceBoundaryClause(semanticBlock, index)) {
            push(index);
            segmentStart = index + 1;
            continue;
        }
        if (character === '•' && index > segmentStart) {
            push(index);
            segmentStart = index;
            continue;
        }
        if (!sentenceBoundary(semanticBlock, index)) continue;
        push(index + 1);
        segmentStart = index + 1;
    }
    if (segmentStart < semanticBlock.length) push(semanticBlock.length);
    return segments;
}

function isLayoutBlockStart(line: string): boolean {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (trimmed.startsWith('|')) return true;
    if (/^(?:[-*•▪◦]+)\s+/.test(trimmed)) return true;
    if (/^(?:\d{1,3}|[A-Za-z])[.)]\s+/.test(trimmed)) return true;
    if (/^(?:WP|W|G)\d+[.)]\s*/i.test(trimmed)) return true;
    return trimmed.length <= 100
        && /:$/.test(trimmed)
        && /[A-Z]/.test(trimmed)
        && trimmed === trimmed.toLocaleUpperCase();
}

function previousLineRequiresContinuation(previousLine: string, currentLine: string): boolean {
    const previous = previousLine.trim();
    const current = currentLine.trim();
    if (!previous || !current) return false;
    if (/^[,.;:)}\]]/.test(current)
        || /^[a-z]/.test(current)
        || /^(?:and|or|to|be|with|under|over|at|of|the|into|from)\b/i.test(current)) return true;
    return /(?:[,/:+-]|\b(?:a|an|and|at|base|cap|engineered|existing|flam|for|from|into|new|of|or|over|the|to|under|with))$/i.test(previous);
}

function standaloneTechnicalLine(line: string): boolean {
    const text = line.replace(STRUCTURAL_PREFIX_RE, '').trim();
    if (!text) return false;
    const terms = constructionTerms(text);
    return ACTION_RE.test(text)
        || hasPositiveSourceBoundary(text)
        || (DOCUMENTED_MEASUREMENT_CUE_RE.test(text) && terms.length > 0)
        || terms.length >= 2
        || KNOWN_NAMED_SYSTEM_RE.test(text)
        || GENERIC_NAMED_SYSTEM_RE.test(text);
}

function layoutBlocks(paragraph: string, absoluteStart: number, paragraphLineStart: number): Array<{
    text: string;
    start: number;
    lineStart: number;
}> {
    const lines = paragraph.split('\n');
    const blocks: Array<{ text: string; start: number; lineStart: number }> = [];
    let currentStart = 0;
    let currentLineStart = paragraphLineStart;
    let cursor = 0;
    const push = (end: number) => {
        const text = paragraph.slice(currentStart, end).replace(/\s+$/, '');
        if (text.trim()) blocks.push({ text, start: absoluteStart + currentStart, lineStart: currentLineStart });
    };
    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index] || '';
        const previousLine = index > 0 ? (lines[index - 1] || '') : '';
        const startsExplicitBlock = isLayoutBlockStart(line);
        const startsTechnicalAnnotation = index > 0
            && standaloneTechnicalLine(line)
            && !previousLineRequiresContinuation(previousLine, line);
        if (index > 0 && (startsExplicitBlock || startsTechnicalAnnotation)) {
            push(cursor - 1);
            currentStart = cursor;
            currentLineStart = paragraphLineStart + index;
        }
        cursor += line.length + (index < lines.length - 1 ? 1 : 0);
    }
    push(paragraph.length);
    return blocks;
}

function segmentSource(content: string): SourceSegment[] {
    const segments: SourceSegment[] = [];
    let paragraphStart = 0;
    let paragraphLineStart = 1;
    const boundary = /\n{2,}/g;
    const pushParagraph = (end: number) => {
        let start = paragraphStart;
        while (start < end && /\s/.test(content[start] || '')) start += 1;
        let trimmedEnd = end;
        while (trimmedEnd > start && /\s/.test(content[trimmedEnd - 1] || '')) trimmedEnd -= 1;
        if (trimmedEnd <= start) return;
        const skippedLines = (content.slice(paragraphStart, start).match(/\n/g) || []).length;
        const paragraph = content.slice(start, trimmedEnd);
        for (const block of layoutBlocks(paragraph, start, paragraphLineStart + skippedLines)) {
            segments.push(...semanticSentenceSegments(block.text, block.start, block.lineStart));
        }
    };
    let match: RegExpExecArray | null;
    while ((match = boundary.exec(content)) !== null) {
        pushParagraph(match.index);
        const consumed = content.slice(paragraphStart, match.index + match[0].length);
        paragraphLineStart += (consumed.match(/\n/g) || []).length;
        paragraphStart = match.index + match[0].length;
    }
    pushParagraph(content.length);
    return segments;
}

/**
 * Recover a PDF/table wrap where the package line and its by-others predicate
 * were split into adjacent semantic segments. The recovery is deliberately
 * bounded: the predicate must be package-less, the immediately preceding
 * segment must end as an unfinished clause, and the intervening source bytes
 * may contain layout whitespace/pipes only. Exact source offsets and text are
 * recomposed from the immutable source, so snapshot identity remains honest.
 */
function recoverAdjacentSourceBoundaryPredicates(
    segments: readonly SourceSegment[],
    sourceContent: string,
): SourceSegment[] {
    const recovered: SourceSegment[] = [];
    for (const current of segments) {
        const previous = recovered.at(-1);
        const packageLessPredicate = ORPHAN_SOURCE_BOUNDARY_PREDICATE_RE.test(current.text);
        const previousIsUnfinished = previous
            && CONSTRUCTION_RE.test(previous.text)
            && /\b(?:and|be|by|new|or|the|to|with)\s*[,;:-]?$/i.test(previous.text);
        const intervening = previous
            ? sourceContent.slice(previous.end, current.start)
            : '';
        const layoutAdjacent = previous
            && current.start >= previous.end
            && intervening.length <= 24
            && /^[\s|,;:-]*$/.test(intervening);
        if (!previous || !packageLessPredicate || !previousIsUnfinished || !layoutAdjacent) {
            recovered.push(current);
            continue;
        }
        recovered[recovered.length - 1] = {
            text: `${previous.text} ${current.text}`.replace(/\s+/g, ' ').trim(),
            sourceText: sourceContent.slice(previous.start, current.end),
            start: previous.start,
            end: current.end,
            lineStart: previous.lineStart,
            lineEnd: current.lineEnd,
        };
    }
    return recovered;
}

function constructionTerms(text: string): string[] {
    return [...new Set((text.match(CONSTRUCTION_TERMS_RE) || [])
        .map(term => term.toLocaleLowerCase().replace(/\s+/g, ' ')))];
}

function hasPositiveSourceBoundary(text: string): boolean {
    return SOURCE_BOUNDARY_RE.test(text)
        && !NEGATED_SOURCE_BOUNDARY_RE.test(text)
        && !ADMINISTRATIVE_SOURCE_BOUNDARY_RE.test(text);
}

function authorityCues(text: string, measurements: readonly RawMeasurement[]): IncorporatedAuthorityCue[] {
    const cues: IncorporatedAuthorityCue[] = [];
    const terms = constructionTerms(text);
    if (ACTION_RE.test(text)) cues.push('action');
    if (NORMATIVE_RE.test(text)) cues.push('normative');
    if (CONSTRUCTION_RE.test(text)) cues.push('construction');
    if (KNOWN_NAMED_SYSTEM_RE.test(text) || GENERIC_NAMED_SYSTEM_RE.test(text)) cues.push('named_system');
    if (measurements.length > 0) cues.push('measurement');
    if (hasPositiveSourceBoundary(text)) cues.push('source_boundary');
    if (TEST_HOLD_POINT_RE.test(text)) cues.push('test_hold_point');
    if (terms.length >= 2) cues.push('technical_sequence');
    const wordCount = text.match(/[A-Za-z0-9][A-Za-z0-9-]*/g)?.length || 0;
    if (terms.length >= 1 && wordCount >= 2 && text.length <= 240) cues.push('technical_annotation');
    return cues;
}

function isWorkBearingRequirement(text: string, cues: readonly IncorporatedAuthorityCue[]): boolean {
    const trimmed = text.trim();
    const headingOnly = /\b(?:DESIGN|DETAILS?|LEGEND|NOTES?|PLAN|SECTION|ELEVATION)\s*$/i.test(trimmed)
        || (/:$/.test(trimmed) && trimmed === trimmed.toLocaleUpperCase());
    if (text.length < 5 || ADMINISTRATIVE_LINE_RE.test(text) || headingOnly) return false;
    const cueSet = new Set(cues);
    if (cueSet.has('source_boundary') && sourceBoundarySubjectForLineage(trimmed)) return true;
    const technical = cueSet.has('construction') || cueSet.has('named_system') || cueSet.has('test_hold_point');
    if (!technical) return false;
    return cueSet.has('action')
        || cueSet.has('normative')
        || cueSet.has('measurement')
        || cueSet.has('named_system')
        || cueSet.has('source_boundary')
        || cueSet.has('test_hold_point')
        || cueSet.has('technical_sequence')
        || cueSet.has('technical_annotation');
}

function measurementCanonicalUnit(raw: string): { kind: IncorporatedMeasurementKind; unit: string } {
    const unit = raw.toLocaleLowerCase().replace(/\s+/g, ' ').replace(/\.$/, '');
    if (/^(?:mm|millimet)/.test(unit)) return { kind: 'length', unit: 'mm' };
    if (/^(?:cm|centimet)/.test(unit)) return { kind: 'length', unit: 'cm' };
    if (/^(?:m²|m2|sqm|sq\.? m|square met)/.test(unit)) return { kind: 'area', unit: 'm2' };
    if (/^(?:panels?|items?|each|ea)/.test(unit)) return { kind: 'count', unit: 'item' };
    if (/^sheets?/.test(unit)) return { kind: 'count', unit: 'sheet' };
    if (/^bags?/.test(unit)) return { kind: 'count', unit: 'bag' };
    if (/^(?:lit|ltr|l$)/.test(unit)) return { kind: 'volume', unit: 'l' };
    if (/^(?:kg|kilogram)/.test(unit)) return { kind: 'mass', unit: 'kg' };
    if (/^(?:hour|hr)/.test(unit)) return { kind: 'duration', unit: 'h' };
    return { kind: 'length', unit: 'm' };
}

const PACKAGE_CAPACITY_CONTAINER_RE = /^\s*(?:[a-z][a-z-]*\s+){0,6}(?:bags?|packs?|packets?|pails?|tubs?|buckets?|containers?|drums?|cans?|kits?|tins?|bottles?)\b/i;
const PACKAGE_COUNT_BEFORE_CAPACITY_RE = /(?:^|[^A-Za-z0-9])(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?\s*(?:bags?|packs?|packets?|pails?|tubs?|buckets?|containers?|drums?|cans?|kits?|tins?|bottles?)\b[^.!?;\n]{0,96}$/i;
const EXPLICIT_TOTAL_BEFORE_RE = /\b(?:total|combined|overall)\s*$/i;
const EXPLICIT_TOTAL_AFTER_RE = /^\s*(?:total|combined|overall)\b/i;
const APPLICATION_RATE_AFTER_RE = /^\s*(?:\/|[^.!?;\n]{0,48}\b(?:per|each|every)\b)/i;
const APPLICATION_RATE_BEFORE_RE = /\b(?:each|every)\b[^.!?;\n]{0,48}$/i;
const NESTED_MEASUREMENT_BEFORE_RATE_RE = /(?<![A-Za-z0-9])(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?\s*-?\s*(?:mm|millimet(?:re|er)s?|cm|centimet(?:re|er)s?|m²|m2|sqm|sq\.?\s*m|square\s+met(?:re|er)s?|m|met(?:re|er)s?|panels?|sheets?|items?|each|ea|bags?|lit(?:re|er)s?|ltr?s?|l|kg|kgs|kilograms?|hours?|hrs?)(?![A-Za-z0-9])/i;
const NON_TOTAL_MASS_VOLUME_AFTER_RE = /^\s*(?:(?:[a-z][a-z-]*\s+){0,4}(?:loads?|capacit(?:y|ies)|ratings?|tanks?|cylinders?|vessels?|reservoirs?))\b/i;
const NON_TOTAL_MASS_VOLUME_BEFORE_RE = /\b(?:capable\s+of\s+supporting|supports?|supporting|(?:rated|tested?)\s+(?:at|to|for)|(?:(?:point|proof|test|working|safe\s+working|design|pull[- ]?out)\s+)?(?:loads?|capacit(?:y|ies)|ratings?)\s*(?:of|at|to|for)?)\s+(?:an?\s+)?$/i;
const MATERIAL_USAGE_ACTION_BEFORE_RE = /\b(?:apply|coat|mix|place|provide|spread|supply|use)\b[^.!?;\n]{0,72}$/i;
const MATERIAL_USAGE_NOUN_SOURCE = 'adhesives?|aggregates?|cements?|compounds?|concretes?|coatings?|epox(?:y|ies)|fillers?|grouts?|membranes?|mortars?|paints?|primers?|renders?|resins?|sands?|sealants?|slurr(?:y|ies)|solvents?|water';
const MATERIAL_USAGE_BEFORE_RE = new RegExp(`\\b(?:${MATERIAL_USAGE_NOUN_SOURCE})\\b[^.!?;\\n]{0,32}$`, 'i');
const MATERIAL_USAGE_AFTER_RE = new RegExp(`^\\s*(?:of\\s+)?(?=[^.!?;\\n]{0,64}\\b(?:${MATERIAL_USAGE_NOUN_SOURCE})\\b)`, 'i');

function isApplicationRate(text: string, end: number): boolean {
    const after = text.slice(end, Math.min(text.length, end + 64));
    if (EXPLICIT_TOTAL_AFTER_RE.test(after)) return false;
    const rateIndex = after.search(/\b(?:per|each|every)\b/i);
    if (rateIndex >= 0 && NESTED_MEASUREMENT_BEFORE_RATE_RE.test(after.slice(0, rateIndex))) return false;
    return APPLICATION_RATE_AFTER_RE.test(after);
}

function isApplicationRateOrSinglePackCapacity(
    text: string,
    start: number,
    end: number,
    kind: IncorporatedMeasurementKind,
    { explicitMultiplier = false }: { explicitMultiplier?: boolean } = {},
): boolean {
    const before = text.slice(Math.max(0, start - 128), start);
    const after = text.slice(end, Math.min(text.length, end + 64));
    const explicitlyTotalled = EXPLICIT_TOTAL_BEFORE_RE.test(before) || EXPLICIT_TOTAL_AFTER_RE.test(after);
    if (!explicitlyTotalled && (APPLICATION_RATE_BEFORE_RE.test(before)
        || isApplicationRate(text, end))) return true;
    if (kind !== 'mass' && kind !== 'volume') return false;
    if (NON_TOTAL_MASS_VOLUME_BEFORE_RE.test(before) || NON_TOTAL_MASS_VOLUME_AFTER_RE.test(after)) return true;
    if (explicitlyTotalled || explicitMultiplier) return false;
    if (PACKAGE_CAPACITY_CONTAINER_RE.test(after)
        || /^\s*each\b/i.test(after)
        || PACKAGE_COUNT_BEFORE_CAPACITY_RE.test(before)) return true;
    const materialUsage = MATERIAL_USAGE_ACTION_BEFORE_RE.test(before)
        && (MATERIAL_USAGE_BEFORE_RE.test(before) || MATERIAL_USAGE_AFTER_RE.test(after));
    return !materialUsage;
}

function rangesOverlap(
    left: Pick<RawMeasurement, 'sourceStart' | 'sourceEnd'>,
    right: Pick<RawMeasurement, 'sourceStart' | 'sourceEnd'>,
): boolean {
    return left.sourceStart < right.sourceEnd && right.sourceStart < left.sourceEnd;
}

function extractMeasurements(segment: SourceSegment): RawMeasurement[] {
    const found: RawMeasurement[] = [];
    // Parse the exact source snippet, not its display-cleaned equivalent. This
    // keeps offsets correct when layout extraction leaves bullets or table
    // pipes before the semantic requirement text.
    const measurementText = segment.sourceText;
    const addRatio = (
        match: RegExpExecArray,
        kind: Extract<IncorporatedMeasurementKind, 'fall_ratio' | 'fall_percent'>,
        numeratorRaw: string,
        denominatorRaw?: string,
    ) => {
        const localStart = match.index;
        const localEnd = localStart + match[0].length;
        const numerator = Number(numeratorRaw);
        const denominator = denominatorRaw == null ? undefined : Number(denominatorRaw);
        if (!Number.isFinite(numerator) || numerator <= 0) return;
        if (denominator != null && (!Number.isFinite(denominator) || denominator <= 0)) return;
        found.push({
            text: match[0],
            kind,
            ...(kind === 'fall_percent'
                ? { value: numerator, unit: '%' }
                : { numerator, denominator }),
            canonical: kind === 'fall_percent' ? `${numerator}% fall` : `${numerator}:${denominator} fall`,
            sourceStart: segment.start + localStart,
            sourceEnd: segment.start + localEnd,
        });
    };

    for (const pattern of [FALL_COLON_RATIO_RE, FALL_IN_RATIO_RE]) {
        pattern.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = pattern.exec(measurementText)) !== null) {
            const ratioContext = match[0];
            const nearby = measurementText.slice(Math.max(0, match.index - 16), match.index + match[0].length + 16);
            // A bare drawing scale is not a fall requirement. The ratio's local
            // wording must itself say fall, gradient or slope; a nearby screed
            // noun cannot turn a scale into a pricing measurement.
            if (!/\b(?:falls?|gradient|slope)\b/i.test(`${ratioContext} ${nearby}`)) continue;
            addRatio(match, 'fall_ratio', match[1] || '', match[2]);
        }
    }

    FALL_PERCENT_RE.lastIndex = 0;
    let percentMatch: RegExpExecArray | null;
    while ((percentMatch = FALL_PERCENT_RE.exec(measurementText)) !== null) {
        addRatio(percentMatch, 'fall_percent', percentMatch[1] || percentMatch[2] || '');
    }

    PACKAGE_MULTIPLIER_RE.lastIndex = 0;
    let packageMatch: RegExpExecArray | null;
    while ((packageMatch = PACKAGE_MULTIPLIER_RE.exec(measurementText)) !== null) {
        const packageCount = Number((packageMatch[1] || '').replace(/,/g, ''));
        const packageCapacity = Number((packageMatch[2] || '').replace(/,/g, ''));
        if (!Number.isFinite(packageCount) || packageCount <= 0
            || !Number.isFinite(packageCapacity) || packageCapacity <= 0) continue;
        const localStart = packageMatch.index;
        const localEnd = localStart + packageMatch[0].length;
        const canonicalUnit = measurementCanonicalUnit(packageMatch[3] || '');
        if (!['mass', 'volume'].includes(canonicalUnit.kind)) continue;
        if (isApplicationRateOrSinglePackCapacity(
            measurementText,
            localStart,
            localEnd,
            canonicalUnit.kind,
            { explicitMultiplier: true },
        )) continue;
        const value = Number((packageCount * packageCapacity).toFixed(4));
        found.push({
            text: packageMatch[0],
            kind: canonicalUnit.kind,
            value,
            unit: canonicalUnit.unit,
            canonical: `${packageCount} x ${packageCapacity} ${canonicalUnit.unit} = ${value} ${canonicalUnit.unit} total`,
            sourceStart: segment.start + localStart,
            sourceEnd: segment.start + localEnd,
        });
    }

    BASIC_MEASUREMENT_RE.lastIndex = 0;
    let basicMatch: RegExpExecArray | null;
    while ((basicMatch = BASIC_MEASUREMENT_RE.exec(measurementText)) !== null) {
        const localStart = basicMatch.index;
        const localEnd = localStart + basicMatch[0].length;
        const value = Number((basicMatch[1] || '').replace(/,/g, ''));
        if (!Number.isFinite(value) || value <= 0) continue;
        const canonicalUnit = measurementCanonicalUnit(basicMatch[2] || '');
        if (isApplicationRateOrSinglePackCapacity(
            measurementText,
            localStart,
            localEnd,
            canonicalUnit.kind,
        )) continue;
        const candidate: RawMeasurement = {
            text: basicMatch[0],
            kind: canonicalUnit.kind,
            value,
            unit: canonicalUnit.unit,
            canonical: `${value} ${canonicalUnit.unit}`,
            sourceStart: segment.start + localStart,
            sourceEnd: segment.start + localEnd,
        };
        if (!found.some(existing => rangesOverlap(existing, candidate))) found.push(candidate);
    }

    return found.sort((left, right) => left.sourceStart - right.sourceStart || left.sourceEnd - right.sourceEnd);
}

function sourceProjection(requirements: readonly IncorporatedAuthorityRequirementV1[]): string {
    return JSON.stringify(requirements.map(requirement => ({
        version: requirement.version,
        id: requirement.id,
        authority: requirement.authority,
        sourceId: requirement.sourceId,
        sourceName: requirement.sourceName,
        sourceContentFingerprint: requirement.sourceContentFingerprint,
        relationshipFingerprint: requirement.relationshipFingerprint,
        ordinal: requirement.ordinal,
        pageOrdinal: requirement.pageOrdinal,
        text: requirement.text,
        cues: requirement.cues,
        byOthers: requirement.byOthers,
        sourceBoundary: requirement.sourceBoundary,
        sourceRef: requirement.sourceRef,
        // PostgreSQL jsonb deliberately canonicalises object-key order. Build
        // nested values back into the V1 schema order before fingerprinting so
        // a save/reload cannot invalidate an otherwise immutable snapshot.
        sourceSnippet: {
            text: requirement.sourceSnippet.text,
            start: requirement.sourceSnippet.start,
            end: requirement.sourceSnippet.end,
            lineStart: requirement.sourceSnippet.lineStart,
            lineEnd: requirement.sourceSnippet.lineEnd,
        },
        measurements: requirement.measurements.map(measurement => ({
            version: measurement.version,
            id: measurement.id,
            sourceRef: measurement.sourceRef,
            sourceId: measurement.sourceId,
            text: measurement.text,
            kind: measurement.kind,
            value: measurement.value,
            unit: measurement.unit,
            canonical: measurement.canonical,
            sourceStart: measurement.sourceStart,
            sourceEnd: measurement.sourceEnd,
        })),
    })));
}

function aggregateSourceIdentity(sources: readonly IncorporatedAuthoritySourceV1[]): string {
    return JSON.stringify(sources.map(source => ({
        version: source.version,
        sourceId: source.sourceId,
        sourceName: source.sourceName,
        classification: source.classification,
        relationshipFingerprint: source.relationshipFingerprint,
        referenceStatementFingerprint: source.referenceStatementFingerprint,
        sourceLength: source.sourceLength,
        sourceFingerprint: source.sourceFingerprint,
        pageCount: source.pageCount,
        extractedPageCount: source.extractedPageCount,
        pageOrdinals: source.pageOrdinals,
        projectionLength: source.projectionLength,
        projectionFingerprint: source.projectionFingerprint,
        requirementCount: source.requirementCount,
        complete: source.complete,
        truncated: source.truncated,
    })));
}

function calculateSnapshotFingerprint(input: {
    sourceCount: number;
    requirementCount: number;
    sourceLength: number;
    sourceFingerprint: string;
    projectionLength: number;
    projectionFingerprint: string;
}): string {
    return fingerprintIncorporatedAuthorityContent(JSON.stringify({
        version: 1,
        kind: 'incorporated-technical-authority-snapshot',
        authority: 'technical-method-only-not-billable',
        ...input,
    }));
}

function validateSourceInput(input: IncorporatedTechnicalDocumentInput):
    | { ok: true; source: ValidatedSource }
    | { ok: false; result: IncorporatedAuthorityBuildResult } {
    const sourceId = String(input?.sourceId || '').trim();
    const sourceName = String(input?.sourceName || input?.name || '').trim();
    const relationshipFingerprint = String(input?.relationshipFingerprint || '').trim();
    const expectedFingerprint = String(input?.sourceContentFingerprint || '').trim();
    if (!sourceId || !sourceName || !relationshipFingerprint || !expectedFingerprint.startsWith('qm-content-v1:')) {
        return {
            ok: false,
            result: buildFailure('source-metadata-invalid', 'Source id, name, relationship fingerprint and content fingerprint are required.', { sourceId }),
        };
    }
    const content = normaliseAuthorityText(input.content);
    if (!content) {
        return { ok: false, result: buildFailure('source-content-empty', 'An incorporated source has no extracted content.', { sourceId }) };
    }
    const expectedLength = input.normalizedSourceLength ?? input.sourceLength;
    const explicitlyIncomplete = input.truncated === true
        || input.contentComplete === false
        || input.contentTruncated === true
        || EXPLICIT_TRUNCATION_RE.test(content)
        || (Number.isFinite(expectedLength) && Number(expectedLength) > content.length);
    if (explicitlyIncomplete) {
        return {
            ok: false,
            result: buildFailure('source-content-truncated', 'An incorporated source is incomplete or truncated.', { sourceId, truncated: true }),
        };
    }
    if (input.complete !== true
        || input.truncated !== false
        || !Number.isSafeInteger(expectedLength)
        || Number(expectedLength) < 1) {
        return {
            ok: false,
            result: buildFailure('source-metadata-invalid', 'Explicit complete=true, truncated=false and an exact positive normalised source length are required.', { sourceId }),
        };
    }
    if (Number(expectedLength) !== content.length) {
        return {
            ok: false,
            result: buildFailure('source-metadata-invalid', 'The declared normalised source length does not match the supplied content.', { sourceId }),
        };
    }
    const rawPageCount = input.sourcePageCount;
    const rawExtractedPageCount = input.extractedPageCount;
    const pageCount = rawPageCount == null
        ? null
        : (Number.isSafeInteger(rawPageCount) && Number(rawPageCount) > 0 ? Number(rawPageCount) : Number.NaN);
    const extractedPageCount = rawExtractedPageCount == null
        ? null
        : (Number.isSafeInteger(rawExtractedPageCount) && Number(rawExtractedPageCount) > 0
            ? Number(rawExtractedPageCount)
            : Number.NaN);
    if (Number.isNaN(pageCount)
        || Number.isNaN(extractedPageCount)
        || (pageCount !== null && extractedPageCount !== null && extractedPageCount < pageCount)) {
        return {
            ok: false,
            result: buildFailure(
                'source-metadata-invalid',
                'PDF page counts must be positive and extracted page coverage must include every declared source page.',
                { sourceId },
            ),
        };
    }
    const actualFingerprint = fingerprintIncorporatedAuthorityContent(content);
    if (actualFingerprint !== expectedFingerprint) {
        return {
            ok: false,
            result: buildFailure('source-content-fingerprint-mismatch', 'The supplied full source does not match its persisted content fingerprint.', { sourceId }),
        };
    }
    return {
        ok: true,
        source: {
            input,
            sourceId,
            sourceName,
            classification: String(input.classification || 'technical_document').trim() || 'technical_document',
            content,
            sourceFingerprint: actualFingerprint,
            pageCount,
            extractedPageCount,
            relationshipFingerprint,
            referenceStatementFingerprint: input.referenceStatementFingerprint
                ? String(input.referenceStatementFingerprint).trim() || null
                : null,
        },
    };
}

function sourcePageMarkers(content: string): Array<{ pageOrdinal: number; start: number }> {
    const markers: Array<{ pageOrdinal: number; start: number }> = [];
    const pattern = /^---\s*Page\s+(\d+)\s*---$/gim;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(content)) !== null) {
        const pageOrdinal = Number(match[1]);
        if (Number.isSafeInteger(pageOrdinal) && pageOrdinal > 0) {
            markers.push({ pageOrdinal, start: match.index });
        }
    }
    return markers;
}

function pageOrdinalForOffset(
    markers: readonly { pageOrdinal: number; start: number }[],
    offset: number,
    pageCount: number | null,
): number | null {
    let pageOrdinal: number | null = pageCount === 1 ? 1 : null;
    for (const marker of markers) {
        if (marker.start > offset) break;
        pageOrdinal = marker.pageOrdinal;
    }
    return pageOrdinal;
}

function requirementsForSource(source: ValidatedSource):
    | { ok: true; requirements: IncorporatedAuthorityRequirementV1[] }
    | { ok: false; result: IncorporatedAuthorityBuildResult } {
    const requirements: IncorporatedAuthorityRequirementV1[] = [];
    const pageMarkers = sourcePageMarkers(source.content);
    const sourceSegments = recoverAdjacentSourceBoundaryPredicates(
        segmentSource(source.content),
        source.content,
    );
    for (const segment of sourceSegments) {
        const measurements = extractMeasurements(segment);
        const cues = authorityCues(segment.text, measurements);
        if (!isWorkBearingRequirement(segment.text, cues)) continue;
        const ordinal = requirements.length + 1;
        const id = `inc-req-v1:${fingerprintIncorporatedAuthorityContent(JSON.stringify({
            sourceId: source.sourceId,
            sourceFingerprint: source.sourceFingerprint,
            start: segment.start,
            end: segment.end,
            text: segment.text,
        })).slice('qm-content-v1:'.length)}`;
        const sourceRef = `inc-src-v1:${fingerprintIncorporatedAuthorityContent(`${source.sourceId}\n${source.sourceFingerprint}`)
            .slice('qm-content-v1:'.length)}:${segment.start}-${segment.end}`;
        const requirementMeasurements: IncorporatedAuthorityMeasurementV1[] = measurements.map((measurement, index) => ({
            version: 1,
            id: `inc-measure-v1:${fingerprintIncorporatedAuthorityContent(`${id}\n${index}\n${measurement.canonical}`)
                .slice('qm-content-v1:'.length)}`,
            sourceRef: id,
            sourceId: source.sourceId,
            ...measurement,
        }));
        requirements.push({
            version: 1,
            id,
            authority: 'technical-method-only-not-billable',
            sourceId: source.sourceId,
            sourceName: source.sourceName,
            sourceContentFingerprint: source.sourceFingerprint,
            relationshipFingerprint: source.relationshipFingerprint,
            ordinal,
            pageOrdinal: pageOrdinalForOffset(pageMarkers, segment.start, source.pageCount),
            text: segment.text,
            cues,
            byOthers: cues.includes('source_boundary'),
            sourceBoundary: cues.includes('source_boundary') ? 'by_others' : null,
            sourceRef,
            sourceSnippet: {
                text: segment.sourceText,
                start: segment.start,
                end: segment.end,
                lineStart: segment.lineStart,
                lineEnd: segment.lineEnd,
            },
            measurements: requirementMeasurements,
        });
    }
    if (requirements.length === 0) {
        return {
            ok: false,
            result: buildFailure('no-requirements', 'An incorporated technical source yielded no conservative work-bearing requirements.', { sourceId: source.sourceId }),
        };
    }
    return { ok: true, requirements };
}

/**
 * Build an immutable, deterministic V1 authority snapshot. Success means every
 * supplied source was full, fingerprint-consistent and produced at least one
 * work-bearing technical requirement. Source size never changes authority:
 * prompt transport is chunked later and no source is truncated to force success.
 */
export function buildIncorporatedAuthoritySnapshot({
    documents,
}: {
    documents: readonly IncorporatedTechnicalDocumentInput[];
}): IncorporatedAuthorityBuildResult {
    if (!Array.isArray(documents) || documents.length === 0) {
        return buildFailure('no-sources', 'At least one full incorporated technical source is required.');
    }
    const validated: ValidatedSource[] = [];
    const sourceIds = new Set<string>();
    const sourceFingerprints = new Set<string>();
    for (const input of documents) {
        const result = validateSourceInput(input);
        if (!result.ok) return result.result;
        const source = result.source;
        if (sourceIds.has(source.sourceId)) {
            return buildFailure('duplicate-source-id', 'The incorporated source set contains a duplicate source id.', { sourceId: source.sourceId });
        }
        if (sourceFingerprints.has(source.sourceFingerprint)) {
            return buildFailure('duplicate-source-content', 'The same incorporated source content was supplied more than once.', { sourceId: source.sourceId });
        }
        sourceIds.add(source.sourceId);
        sourceFingerprints.add(source.sourceFingerprint);
        validated.push(source);
    }
    validated.sort((left, right) => left.sourceId.localeCompare(right.sourceId));

    const totalSourceLength = validated.reduce((total, source) => total + source.content.length, 0);
    const requirements: IncorporatedAuthorityRequirementV1[] = [];
    const sourceRows: IncorporatedAuthoritySourceV1[] = [];
    for (const source of validated) {
        const extracted = requirementsForSource(source);
        if (!extracted.ok) return extracted.result;
        requirements.push(...extracted.requirements);
        const projection = sourceProjection(extracted.requirements);
        sourceRows.push({
            version: 1,
            sourceId: source.sourceId,
            sourceName: source.sourceName,
            classification: source.classification,
            relationshipFingerprint: source.relationshipFingerprint,
            referenceStatementFingerprint: source.referenceStatementFingerprint,
            sourceLength: source.content.length,
            sourceFingerprint: source.sourceFingerprint,
            pageCount: source.pageCount,
            extractedPageCount: source.extractedPageCount,
            pageOrdinals: source.pageCount === null
                ? []
                : Array.from({ length: source.pageCount }, (_, index) => index + 1),
            projectionLength: projection.length,
            projectionFingerprint: fingerprintIncorporatedAuthorityContent(projection),
            requirementCount: extracted.requirements.length,
            complete: true,
            truncated: false,
        });
    }

    const projection = sourceProjection(requirements);
    const aggregateIdentity = aggregateSourceIdentity(sourceRows);
    const sourceFingerprint = fingerprintIncorporatedAuthorityContent(aggregateIdentity);
    const projectionFingerprint = fingerprintIncorporatedAuthorityContent(projection);
    const fingerprintInput = {
        sourceCount: sourceRows.length,
        requirementCount: requirements.length,
        sourceLength: totalSourceLength,
        sourceFingerprint,
        projectionLength: projection.length,
        projectionFingerprint,
    };
    const snapshot: IncorporatedAuthoritySnapshotV1 = {
        version: 1,
        kind: 'incorporated-technical-authority-snapshot',
        authority: 'technical-method-only-not-billable',
        complete: true,
        truncated: false,
        ...fingerprintInput,
        snapshotFingerprint: calculateSnapshotFingerprint(fingerprintInput),
        sources: sourceRows,
        requirements,
    };
    return freezeDeep({ ok: true as const, snapshot: freezeDeep(snapshot) });
}

function snapshotIntegrityValid(snapshot: unknown): snapshot is IncorporatedAuthoritySnapshotV1 {
    if (!snapshot || typeof snapshot !== 'object') return false;
    const candidate = snapshot as Partial<IncorporatedAuthoritySnapshotV1>;
    if (candidate.version !== 1
        || candidate.kind !== 'incorporated-technical-authority-snapshot'
        || candidate.authority !== 'technical-method-only-not-billable'
        || candidate.complete !== true
        || candidate.truncated !== false
        || !Array.isArray(candidate.sources)
        || !Array.isArray(candidate.requirements)
        || candidate.sources.length === 0
        || candidate.requirements.length === 0
        || !Number.isSafeInteger(candidate.sourceLength)
        || !Number.isSafeInteger(candidate.projectionLength)) return false;
    const sources = candidate.sources as readonly IncorporatedAuthoritySourceV1[];
    const requirements = candidate.requirements as readonly IncorporatedAuthorityRequirementV1[];
    if (new Set(sources.map(source => source.sourceId)).size !== sources.length
        || sources.some((source, index) => index > 0 && sources[index - 1]!.sourceId.localeCompare(source.sourceId) >= 0)) return false;
    if (new Set(requirements.map(requirement => requirement.id)).size !== requirements.length) return false;
    const measurementIds = new Set<string>();
    for (const requirement of requirements) {
        const source = sources.find(candidateSource => candidateSource.sourceId === requirement.sourceId);
        if (!source
            || requirement.version !== 1
            || requirement.authority !== 'technical-method-only-not-billable'
            || !/^inc-req-v1:[a-f0-9]{16}$/.test(requirement.id)
            || requirement.sourceName !== source.sourceName
            || requirement.sourceContentFingerprint !== source.sourceFingerprint
            || requirement.relationshipFingerprint !== source.relationshipFingerprint
            || !Number.isSafeInteger(requirement.ordinal)
            || requirement.ordinal < 1
            || (requirement.pageOrdinal !== null
                && (!Number.isSafeInteger(requirement.pageOrdinal) || requirement.pageOrdinal < 1))
            || (source.pageCount !== null
                && requirement.pageOrdinal !== null
                && requirement.pageOrdinal > source.pageCount)
            || !requirement.text
            || !Array.isArray(requirement.cues)
            || requirement.cues.length === 0
            || requirement.byOthers !== (requirement.sourceBoundary === 'by_others')
            || requirement.byOthers !== requirement.cues.includes('source_boundary')
            || !requirement.sourceRef
            || !requirement.sourceSnippet
            || !Number.isSafeInteger(requirement.sourceSnippet.start)
            || !Number.isSafeInteger(requirement.sourceSnippet.end)
            || requirement.sourceSnippet.start < 0
            || requirement.sourceSnippet.end <= requirement.sourceSnippet.start
            || typeof requirement.sourceSnippet.text !== 'string'
            || !requirement.sourceSnippet.text
            || !Number.isSafeInteger(requirement.sourceSnippet.lineStart)
            || !Number.isSafeInteger(requirement.sourceSnippet.lineEnd)
            || requirement.sourceSnippet.lineStart < 1
            || requirement.sourceSnippet.lineEnd < requirement.sourceSnippet.lineStart
            || !Array.isArray(requirement.measurements)) return false;
        for (const measurement of requirement.measurements) {
            const relativeStart = measurement.sourceStart - requirement.sourceSnippet.start;
            const relativeEnd = measurement.sourceEnd - requirement.sourceSnippet.start;
            if (measurement.version !== 1
                || !/^inc-measure-v1:[a-f0-9]{16}$/.test(measurement.id)
                || measurementIds.has(measurement.id)
                || measurement.sourceRef !== requirement.id
                || measurement.sourceId !== requirement.sourceId
                || relativeStart < 0
                || relativeEnd <= relativeStart
                || relativeEnd > requirement.sourceSnippet.text.length
                || requirement.sourceSnippet.text.slice(relativeStart, relativeEnd) !== measurement.text
                || !measurement.canonical) return false;
            measurementIds.add(measurement.id);
        }
    }
    const projection = sourceProjection(requirements);
    if (candidate.sourceCount !== sources.length
        || candidate.requirementCount !== requirements.length
        || candidate.sourceLength !== sources.reduce((total, source) => total + source.sourceLength, 0)
        || candidate.projectionLength !== projection.length
        || candidate.projectionFingerprint !== fingerprintIncorporatedAuthorityContent(projection)) return false;
    for (const source of sources) {
        const sourceRequirements = requirements.filter(requirement => requirement.sourceId === source.sourceId);
        const sourceProjectionText = sourceProjection(sourceRequirements);
        if (source.version !== 1
            || source.complete !== true
            || source.truncated !== false
            || !source.sourceId
            || !source.sourceName
            || !source.classification
            || !source.relationshipFingerprint
            || !source.sourceFingerprint.startsWith('qm-content-v1:')
            || !Number.isSafeInteger(source.sourceLength)
            || source.sourceLength < 1
            || (source.pageCount !== null
                && (!Number.isSafeInteger(source.pageCount) || source.pageCount < 1))
            || (source.extractedPageCount !== null
                && (!Number.isSafeInteger(source.extractedPageCount) || source.extractedPageCount < 1))
            || (source.pageCount !== null
                && source.extractedPageCount !== null
                && source.extractedPageCount < source.pageCount)
            || !Array.isArray(source.pageOrdinals)
            || JSON.stringify(source.pageOrdinals) !== JSON.stringify(source.pageCount === null
                ? []
                : Array.from({ length: source.pageCount }, (_, index) => index + 1))
            || source.requirementCount !== sourceRequirements.length
            || sourceRequirements.some((requirement, index) => requirement.ordinal !== index + 1)
            || source.projectionLength !== sourceProjectionText.length
            || source.projectionFingerprint !== fingerprintIncorporatedAuthorityContent(sourceProjectionText)) return false;
    }
    const expectedSourceFingerprint = fingerprintIncorporatedAuthorityContent(aggregateSourceIdentity(sources));
    const expectedSnapshotFingerprint = calculateSnapshotFingerprint({
        sourceCount: sources.length,
        requirementCount: requirements.length,
        sourceLength: candidate.sourceLength,
        sourceFingerprint: expectedSourceFingerprint,
        projectionLength: projection.length,
        projectionFingerprint: fingerprintIncorporatedAuthorityContent(projection),
    });
    return candidate.sourceFingerprint === expectedSourceFingerprint
        && candidate.snapshotFingerprint === expectedSnapshotFingerprint;
}

type IncorporatedPromptWireRequirement = Readonly<{
    id: string;
    o: number;
    so: number;
    s: string;
    p: number | null;
    t: string;
    part: number;
    parts: number;
    b?: 'by_others';
    m?: readonly string[];
}>;

function safePromptJson(value: unknown): string {
    // The projection is inserted into an XML-shaped prompt. JSON.parse()
    // restores these source characters exactly for the model-visible values.
    return JSON.stringify(value)
        .replaceAll('&', '\\u0026')
        .replaceAll('<', '\\u003c')
        .replaceAll('>', '\\u003e');
}

function promptWireText(
    snapshot: IncorporatedAuthoritySnapshotV1,
    rows: readonly IncorporatedPromptWireRequirement[],
    chunkOrdinal: number,
    chunkCount: number,
): string {
    const sourceRefs = new Map(snapshot.sources.map((source, index) => [source.sourceId, `S${index + 1}`]));
    const touchedRefs = new Set(rows.map(row => row.s));
    return safePromptJson({
        v: 1,
        k: 'incorporated-technical-authority-chunk',
        snapshot: snapshot.snapshotFingerprint,
        authority: snapshot.authority,
        complete: true,
        truncated: false,
        chunk: chunkOrdinal,
        chunks: chunkCount,
        keys: {
            id: 'requirement_id',
            ordinal: 'requirements_array_position_plus_one',
            source_ordinal: 'one_based_requirement_position_inside_source',
            s: 'source_ref',
            p: 'one_based_pdf_page_or_null',
            t: 'exact_requirement_text_fragment',
            part: 'one_based_text_fragment_position',
            parts: 'text_fragment_count',
            b: 'source_boundary_by_others',
            m: 'canonical_measurements',
        },
        sources: snapshot.sources.flatMap((source, index) => {
            const ref = sourceRefs.get(source.sourceId)!;
            return touchedRefs.has(ref) ? [{ r: ref, id: source.sourceId }] : [];
        }),
        requirements: rows,
    });
}

function promptWireRows(snapshot: IncorporatedAuthoritySnapshotV1): IncorporatedPromptWireRequirement[] {
    const sourceRefs = new Map(snapshot.sources.map((source, index) => [source.sourceId, `S${index + 1}`]));
    return snapshot.requirements.flatMap((requirement, index) => {
        const fragments = Array.from(
            { length: Math.ceil(requirement.text.length / INCORPORATED_AUTHORITY_PROMPT_REQUIREMENT_FRAGMENT_CHARS) },
            (_, fragmentIndex) => requirement.text.slice(
                fragmentIndex * INCORPORATED_AUTHORITY_PROMPT_REQUIREMENT_FRAGMENT_CHARS,
                (fragmentIndex + 1) * INCORPORATED_AUTHORITY_PROMPT_REQUIREMENT_FRAGMENT_CHARS,
            ),
        );
        return fragments.map((fragment, fragmentIndex): IncorporatedPromptWireRequirement => ({
            id: requirement.id,
            o: index + 1,
            so: requirement.ordinal,
            s: sourceRefs.get(requirement.sourceId)!,
            p: requirement.pageOrdinal,
            t: fragment,
            part: fragmentIndex + 1,
            parts: fragments.length,
            ...(requirement.sourceBoundary ? { b: requirement.sourceBoundary } : {}),
            ...(fragmentIndex === 0 && requirement.measurements.length > 0
                ? { m: requirement.measurements.map(measurement => measurement.canonical) }
                : {}),
        }));
    });
}

function coverageReceiptFor(
    snapshot: IncorporatedAuthoritySnapshotV1,
    chunks: readonly IncorporatedAuthorityPromptProjectionChunkV1[],
    chunkRows: readonly (readonly IncorporatedPromptWireRequirement[])[],
    projectionFingerprint: string,
): IncorporatedAuthorityPromptCoverageReceiptV1 {
    const chunksReceipt = chunks.map((chunk, index) => ({
        chunkId: chunk.chunkId,
        chunkOrdinal: chunk.chunkOrdinal,
        projectionLength: chunk.projectionLength,
        projectionFingerprint: chunk.projectionFingerprint,
        sourceIds: [...chunk.sourceIds],
        sourceCoverage: chunk.sourceIds.map(sourceId => {
            const source = snapshot.sources.find(row => row.sourceId === sourceId)!;
            const sourceRows = chunkRows[index]!.filter(row => (
                snapshot.requirements[row.o - 1]!.sourceId === sourceId
            ));
            return {
                sourceId,
                sourceLength: source.sourceLength,
                sourceFingerprint: source.sourceFingerprint,
                pageOrdinals: [...new Set(sourceRows
                    .map(row => row.p)
                    .filter((value): value is number => value !== null))].sort((left, right) => left - right),
                sourceRequirementOrdinals: [...new Set(sourceRows.map(row => row.so))].sort((left, right) => left - right),
                projectionRequirementOrdinals: [...new Set(sourceRows.map(row => row.o))].sort((left, right) => left - right),
            };
        }),
        requirementOrdinals: [...chunk.requirementOrdinals],
        requirementIds: [...chunk.requirementIds],
        requirementCount: chunk.requirementCount,
        requirementFragmentCount: chunk.requirementFragmentCount,
    }));
    const sourcesReceipt = snapshot.sources.map(source => {
        const projectionRequirementOrdinals = snapshot.requirements
            .map((requirement, index) => ({ requirement, ordinal: index + 1 }))
            .filter(row => row.requirement.sourceId === source.sourceId)
            .map(row => row.ordinal);
        return {
            sourceId: source.sourceId,
            sourceLength: source.sourceLength,
            sourceFingerprint: source.sourceFingerprint,
            pageCount: source.pageCount,
            extractedPageCount: source.extractedPageCount,
            pageOrdinals: [...source.pageOrdinals],
            requirementCount: source.requirementCount,
            sourceRequirementOrdinals: Array.from({ length: source.requirementCount }, (_, index) => index + 1),
            projectionRequirementOrdinals,
            chunkOrdinals: chunks
                .filter(chunk => chunk.sourceIds.includes(source.sourceId))
                .map(chunk => chunk.chunkOrdinal),
        };
    });
    const withoutFingerprint = {
        version: 1 as const,
        kind: 'incorporated-authority-prompt-coverage-receipt' as const,
        complete: true as const,
        truncated: false as const,
        snapshotFingerprint: snapshot.snapshotFingerprint,
        sourceCount: snapshot.sourceCount,
        sourceLength: snapshot.sourceLength,
        sourceFingerprint: snapshot.sourceFingerprint,
        requirementCount: snapshot.requirementCount,
        chunkCount: chunks.length,
        projectionLength: chunks.reduce((total, chunk) => total + chunk.projectionLength, 0),
        projectionFingerprint,
        coveredRequirementOrdinals: Array.from({ length: snapshot.requirementCount }, (_, index) => index + 1),
        sources: sourcesReceipt,
        chunks: chunksReceipt,
    };
    return {
        ...withoutFingerprint,
        coverageFingerprint: fingerprintIncorporatedAuthorityContent(JSON.stringify(withoutFingerprint)),
    };
}

/**
 * Project every immutable requirement into bounded transport chunks. The 32k
 * value is a per-call envelope, never an ingestion or whole-document ceiling.
 * Long requirements are split into exact, ordered text fragments; the archival
 * snapshot remains unsplit and every fragment is fingerprinted in its chunk.
 */
export function buildIncorporatedAuthorityPromptProjection(
    snapshot: unknown,
): IncorporatedAuthorityPromptProjectionResult {
    if (!snapshotIntegrityValid(snapshot)) {
        return freezeDeep({
            ok: false as const,
            failure: {
                version: 1 as const,
                complete: false as const,
                truncated: false as const,
                code: 'snapshot-invalid' as const,
                message: 'A complete fingerprint-valid authority snapshot is required.',
            },
        });
    }
    const rows = promptWireRows(snapshot);
    const groupedRows: IncorporatedPromptWireRequirement[][] = [];
    let currentRows: IncorporatedPromptWireRequirement[] = [];
    // Leave fixed headroom for the final chunk ordinal/count fields. Sizing is
    // performed on the XML-safe wire representation, not the raw source text.
    const chunkBudget = INCORPORATED_AUTHORITY_PROMPT_PROJECTION_MAX_CHARS - 1_024;
    for (const row of rows) {
        const candidate = [...currentRows, row];
        const candidateText = promptWireText(snapshot, candidate, 999_999, 999_999);
        if (currentRows.length > 0 && candidateText.length > chunkBudget) {
            groupedRows.push(currentRows);
            currentRows = [row];
        } else {
            currentRows = candidate;
        }
        const singleText = promptWireText(snapshot, currentRows, 999_999, 999_999);
        if (singleText.length > INCORPORATED_AUTHORITY_PROMPT_PROJECTION_MAX_CHARS) {
            return freezeDeep({
                ok: false as const,
                failure: {
                    version: 1 as const,
                    complete: false as const,
                    truncated: false as const,
                    code: 'projection-chunk-invalid' as const,
                    message: 'A fingerprinted requirement fragment could not fit in one Stage 3 transport chunk.',
                    chunkOrdinal: groupedRows.length + 1,
                    projectionLength: singleText.length,
                },
            });
        }
    }
    if (currentRows.length > 0) groupedRows.push(currentRows);

    const chunkCount = groupedRows.length;
    const chunks: IncorporatedAuthorityPromptProjectionChunkV1[] = groupedRows.map((chunkRequirementRows, index) => {
        const text = promptWireText(snapshot, chunkRequirementRows, index + 1, chunkCount);
        const projectionFingerprint = fingerprintIncorporatedAuthorityContent(text);
        const requirementOrdinals = [...new Set(chunkRequirementRows.map(row => row.o))].sort((left, right) => left - right);
        const requirementIds = requirementOrdinals.map(ordinal => snapshot.requirements[ordinal - 1]!.id);
        const sourceIds = [...new Set(requirementOrdinals
            .map(ordinal => snapshot.requirements[ordinal - 1]!.sourceId))];
        const pageOrdinals = [...new Set(chunkRequirementRows
            .map(row => row.p)
            .filter((value): value is number => value !== null))].sort((left, right) => left - right);
        return {
            version: 1,
            kind: 'incorporated-authority-prompt-projection-chunk',
            complete: true,
            truncated: false,
            snapshotFingerprint: snapshot.snapshotFingerprint,
            chunkId: `inc-auth-chunk-v1:${projectionFingerprint.slice('qm-content-v1:'.length)}`,
            chunkOrdinal: index + 1,
            chunkCount,
            sourceIds,
            requirementOrdinals,
            requirementIds,
            requirementCount: requirementOrdinals.length,
            requirementFragmentCount: chunkRequirementRows.length,
            pageOrdinals,
            projectionLength: text.length,
            projectionFingerprint,
            text,
        };
    });
    const projectionLength = chunks.reduce((total, chunk) => total + chunk.projectionLength, 0);
    const projectionFingerprint = fingerprintIncorporatedAuthorityContent(JSON.stringify(chunks.map(chunk => ({
        chunkId: chunk.chunkId,
        chunkOrdinal: chunk.chunkOrdinal,
        projectionLength: chunk.projectionLength,
        projectionFingerprint: chunk.projectionFingerprint,
        requirementOrdinals: chunk.requirementOrdinals,
    }))));
    const coverageReceipt = coverageReceiptFor(snapshot, chunks, groupedRows, projectionFingerprint);
    const projection: IncorporatedAuthorityPromptProjectionV1 = {
        version: 1,
        kind: 'incorporated-authority-prompt-projection',
        complete: true,
        truncated: false,
        snapshotFingerprint: snapshot.snapshotFingerprint,
        sourceCount: snapshot.sources.length,
        requirementCount: snapshot.requirements.length,
        chunkCount,
        projectionLength,
        projectionFingerprint,
        text: chunkCount === 1 ? chunks[0]!.text : '',
        chunks,
        coverageReceipt,
    };
    return freezeDeep({ ok: true as const, projection: freezeDeep(projection) });
}

function projectionValidationFailure(
    code: Exclude<IncorporatedAuthorityPromptProjectionValidationResult, { ok: true }>['failure']['code'],
    message: string,
    detail: Partial<Exclude<IncorporatedAuthorityPromptProjectionValidationResult, { ok: true }>['failure']> = {},
): IncorporatedAuthorityPromptProjectionValidationResult {
    return freezeDeep({
        ok: false as const,
        failure: {
            version: 1 as const,
            complete: false as const,
            truncated: false as const,
            code,
            message,
            ...detail,
        },
    });
}

/** Rebuild and compare the complete chunk/receipt contract without trusting it. */
export function validateIncorporatedAuthorityPromptProjection(
    snapshot: unknown,
    projection: unknown,
): IncorporatedAuthorityPromptProjectionValidationResult {
    if (!snapshotIntegrityValid(snapshot)) {
        return projectionValidationFailure('snapshot-invalid', 'The authority snapshot is not fingerprint-valid.');
    }
    if (!projection || typeof projection !== 'object') {
        return projectionValidationFailure('projection-invalid', 'The authority prompt projection is missing or invalid.');
    }
    const candidate = projection as Partial<IncorporatedAuthorityPromptProjectionV1>;
    if (!Array.isArray(candidate.chunks)
        || candidate.complete !== true
        || candidate.truncated !== false
        || candidate.snapshotFingerprint !== snapshot.snapshotFingerprint) {
        return projectionValidationFailure('projection-invalid', 'The authority prompt projection envelope does not match its snapshot.');
    }
    const rebuilt = buildIncorporatedAuthorityPromptProjection(snapshot);
    if (!rebuilt.ok) {
        return projectionValidationFailure('projection-invalid', rebuilt.failure.message);
    }
    const expected = rebuilt.projection;
    if (candidate.chunkCount !== expected.chunkCount || candidate.chunks.length !== expected.chunkCount) {
        return projectionValidationFailure(
            'chunk-count-mismatch',
            'The authority prompt projection does not contain the exact expected chunk count.',
            {
                expectedChunkCount: expected.chunkCount,
                receivedChunkCount: candidate.chunks.length,
            },
        );
    }
    const seenRequirementOrdinals = new Map<number, number>();
    for (const expectedChunk of expected.chunks) {
        const received = candidate.chunks.find(chunk => chunk?.chunkOrdinal === expectedChunk.chunkOrdinal);
        if (!received) {
            return projectionValidationFailure('chunk-missing', 'An authority prompt projection chunk is missing.', {
                chunkOrdinal: expectedChunk.chunkOrdinal,
            });
        }
        if (received.projectionFingerprint !== fingerprintIncorporatedAuthorityContent(received.text)
            || JSON.stringify(received) !== JSON.stringify(expectedChunk)) {
            return projectionValidationFailure('chunk-fingerprint-mismatch', 'An authority prompt projection chunk changed or failed fingerprint validation.', {
                chunkOrdinal: expectedChunk.chunkOrdinal,
            });
        }
        for (const ordinal of received.requirementOrdinals) {
            seenRequirementOrdinals.set(ordinal, (seenRequirementOrdinals.get(ordinal) || 0) + 1);
        }
    }
    for (let ordinal = 1; ordinal <= snapshot.requirementCount; ordinal += 1) {
        const count = seenRequirementOrdinals.get(ordinal) || 0;
        if (count === 0) {
            return projectionValidationFailure('requirement-coverage-missing', 'A requirement is absent from the chunk coverage receipt.', {
                requirementOrdinal: ordinal,
            });
        }
        // A long requirement may span adjacent chunks through exact fragments.
        const expectedCount = expected.chunks.filter(chunk => chunk.requirementOrdinals.includes(ordinal)).length;
        if (count !== expectedCount) {
            return projectionValidationFailure('requirement-coverage-duplicate', 'A requirement has unexpected duplicate chunk coverage.', {
                requirementOrdinal: ordinal,
            });
        }
    }
    if (candidate.projectionLength !== expected.projectionLength
        || candidate.projectionFingerprint !== expected.projectionFingerprint
        || JSON.stringify(candidate.coverageReceipt) !== JSON.stringify(expected.coverageReceipt)) {
        return projectionValidationFailure('coverage-receipt-mismatch', 'The authority chunk coverage receipt does not match the complete snapshot projection.');
    }
    return freezeDeep({ ok: true as const, receipt: expected.coverageReceipt });
}

function stringRefs(value: unknown): string[] | null {
    if (value == null) return [];
    if (!Array.isArray(value) || value.length > 100) return null;
    if (value.some(item => typeof item !== 'string')) return null;
    const refs = value.map(item => item.trim());
    if (refs.some(ref => !ref || ref.length > 240)) return null;
    return [...new Set(refs)].sort((left, right) => left.localeCompare(right));
}

function dispositionFailure(issues: IncorporatedDispositionIssueV1[]): IncorporatedDispositionValidationResult {
    return freezeDeep({
        ok: false as const,
        failure: {
            version: 1 as const,
            complete: false as const,
            issues,
        },
    });
}

/**
 * Validate the AI/human disposition ledger against an immutable snapshot.
 * Every requirement must appear exactly once. Technical source authority alone
 * cannot include work in the builder's price, and a source-marked "by others"
 * requirement is never allowed to become included builder scope.
 */
export function validateIncorporatedRequirementDispositions(
    snapshot: unknown,
    dispositions: readonly IncorporatedRequirementDispositionInput[],
): IncorporatedDispositionValidationResult {
    if (!snapshotIntegrityValid(snapshot)) {
        return dispositionFailure([{
            version: 1,
            code: 'snapshot-invalid',
            message: 'The incorporated authority snapshot is incomplete or fingerprint-invalid.',
        }]);
    }
    const inputRows = Array.isArray(dispositions) ? dispositions : [];
    const requirementsById = new Map(snapshot.requirements.map(requirement => [requirement.id, requirement]));
    const rowsByRequirement = new Map<string, Array<{ index: number; status: string }>>();
    const canonicalByRequirement = new Map<string, IncorporatedRequirementDispositionV1>();
    const issues: IncorporatedDispositionIssueV1[] = [];

    for (let index = 0; index < inputRows.length; index += 1) {
        const raw = inputRows[index];
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
            issues.push({ version: 1, code: 'disposition-not-object', message: 'A disposition row is not an object.', index });
            continue;
        }
        const requirementId = typeof raw.requirement_id === 'string' ? raw.requirement_id.trim() : '';
        if (!requirementId || requirementId.length > 128) {
            issues.push({ version: 1, code: 'requirement-id-missing', message: 'A disposition row has no requirement id.', index });
            continue;
        }
        const status = typeof raw.status === 'string' ? raw.status.trim() : '';
        const existingRows = rowsByRequirement.get(requirementId) || [];
        existingRows.push({ index, status });
        rowsByRequirement.set(requirementId, existingRows);
        const requirement = requirementsById.get(requirementId);
        if (!requirement) {
            issues.push({ version: 1, code: 'unknown-requirement', message: 'A disposition cites an unknown requirement.', requirementId, index });
            continue;
        }
        if (!DISPOSITION_STATUS_SET.has(status)) {
            issues.push({ version: 1, code: 'invalid-status', message: 'A disposition uses a status outside the closed V1 set.', requirementId, index });
            continue;
        }
        const activityRefs = stringRefs(raw.activity_refs);
        const pricingInputRefs = stringRefs(raw.pricing_input_refs);
        let reason = '';
        if (raw.reason != null) {
            if (typeof raw.reason !== 'string'
                || raw.reason.length > 2_000
                || /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(raw.reason)) {
                issues.push({ version: 1, code: 'invalid-reason', message: 'Disposition reason must be a bounded plain string.', requirementId, index });
            } else {
                reason = raw.reason.trim();
            }
        }
        if (activityRefs === null) {
            issues.push({ version: 1, code: 'invalid-activity-refs', message: 'Activity references must be a bounded array of non-empty strings.', requirementId, index });
        }
        if (pricingInputRefs === null) {
            issues.push({ version: 1, code: 'invalid-pricing-input-refs', message: 'Pricing input references must be a bounded array of non-empty strings.', requirementId, index });
        }
        if (status === 'included_in_builder_scope') {
            if (!activityRefs || activityRefs.length === 0) {
                issues.push({ version: 1, code: 'included-activity-refs-missing', message: 'Included builder scope requires at least one activity reference.', requirementId, index });
            }
            if (!pricingInputRefs || pricingInputRefs.length === 0) {
                issues.push({ version: 1, code: 'included-pricing-input-refs-missing', message: 'Included builder scope requires at least one pricing input reference.', requirementId, index });
            }
            if (requirement.byOthers) {
                issues.push({ version: 1, code: 'by-others-included', message: 'A source-marked by-others requirement cannot be included in builder scope.', requirementId, index });
            }
        }
        if (status !== 'included_in_builder_scope' && pricingInputRefs && pricingInputRefs.length > 0) {
            issues.push({
                version: 1,
                code: 'non-included-pricing-input-refs-present',
                message: 'A non-included technical requirement cannot retain billable Pricing input references.',
                requirementId,
                index,
            });
        }
        if (status !== 'included_in_builder_scope'
            && status !== 'coordination_only'
            && activityRefs
            && activityRefs.length > 0) {
            issues.push({
                version: 1,
                code: 'non-coordination-activity-refs-present',
                message: 'Only included or coordination-only dispositions may retain Work Method activity references.',
                requirementId,
                index,
            });
        }
        if (status === 'coordination_only'
            && !requirement.byOthers
            && (!activityRefs || activityRefs.length === 0)) {
            issues.push({
                version: 1,
                code: 'coordination-activity-refs-missing',
                message: 'An ordinary technical-only requirement may be coordinated only through an explicit Work Method activity.',
                requirementId,
                index,
            });
        }
        if (status === 'not_applicable' && !reason) {
            issues.push({ version: 1, code: 'not-applicable-reason-missing', message: 'A not-applicable disposition requires a reason.', requirementId, index });
        }
        if (status === 'excluded_by_source_boundary' && !requirement.byOthers) {
            issues.push({ version: 1, code: 'source-boundary-status-invalid', message: 'Only a source-marked boundary requirement may use excluded_by_source_boundary.', requirementId, index });
        }
        if (!requirement.byOthers
            && status !== 'included_in_builder_scope'
            && status !== 'coordination_only'
            && status !== 'awaiting_builder_decision') {
            issues.push({
                version: 1,
                code: 'builder-work-status-invalid',
                message: 'Ordinary technical work must be included, coordinated through an explicit activity, or left awaiting a builder decision; model prose cannot exclude it.',
                requirementId,
                index,
            });
        }
        canonicalByRequirement.set(requirementId, {
            version: 1,
            requirement_id: requirementId,
            status: status as IncorporatedRequirementDispositionStatus,
            activity_refs: activityRefs || [],
            pricing_input_refs: pricingInputRefs || [],
            reason,
        });
    }

    for (const [requirementId, rows] of rowsByRequirement) {
        if (rows.length <= 1 || !requirementsById.has(requirementId)) continue;
        const statuses = new Set(rows.map(row => row.status));
        issues.push({
            version: 1,
            code: statuses.size > 1 ? 'conflicting-requirement' : 'duplicate-requirement',
            message: statuses.size > 1
                ? 'A requirement has conflicting disposition statuses.'
                : 'A requirement has more than one disposition row.',
            requirementId,
            index: rows[1]?.index,
        });
    }
    for (const requirement of snapshot.requirements) {
        if (!rowsByRequirement.has(requirement.id)) {
            issues.push({
                version: 1,
                code: 'missing-requirement',
                message: 'A source requirement has no disposition.',
                requirementId: requirement.id,
            });
        }
    }
    if (issues.length > 0) return dispositionFailure(issues);

    const canonical = snapshot.requirements.map(requirement => canonicalByRequirement.get(requirement.id)!);
    const dispositionProjection = JSON.stringify(canonical);
    const receipt: IncorporatedRequirementDispositionReceiptV1 = {
        version: 1,
        kind: 'incorporated-requirement-disposition-receipt',
        complete: true,
        snapshotFingerprint: snapshot.snapshotFingerprint,
        requirementCount: snapshot.requirements.length,
        dispositionCount: canonical.length,
        dispositionFingerprint: fingerprintIncorporatedAuthorityContent(dispositionProjection),
        dispositions: canonical,
    };
    return freezeDeep({ ok: true as const, receipt: freezeDeep(receipt) });
}
